import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import type {
  QueriesAlertUnreadAlerts,
  QueriesGetUnreadAlertsResponse,
  SystemAlertsSystemAlertDetailsForUser,
  TransferObjectsResponseDTO,
} from 'client'
import { MESSAGE, STORE } from '~/constants/strings'
import { HNRV2 } from '~/helpers'
import { api } from '~/services'
import { notifyFailure } from '~/utils/notifications'

interface SystemAlertsClientStoreState {
  alerts: QueriesAlertUnreadAlerts[]

  isSystemAlertDialogShowing: boolean

  isSystemAlertViewModalOpen: boolean
  alertDetailsForUser: SystemAlertsSystemAlertDetailsForUser | null
}

export const useSystemAlertsClientStore = defineStore(
  STORE.SYSTEM_ALERTS_CLIENT,
  {
    state: (): SystemAlertsClientStoreState => ({
      alerts: [],

      isSystemAlertDialogShowing: false,

      isSystemAlertViewModalOpen: false,
      alertDetailsForUser: null,
    }),
    getters: {},
    actions: {
      async handleSystemAlertReviewModal() {
        await this.getUnreadAlertsForUser()
        if (this.alerts.length > 0) {
          this.isSystemAlertDialogShowing = true
        }
      },
      async getUnreadAlertsForUser(): Promise<void> {
        const response = await HNRV2<QueriesGetUnreadAlertsResponse>({
          fn: () => api.systemAlerts.apiSystemAlertsUnreadGet(),
          shouldErrorRedirect: false,
        })

        if (response instanceof AxiosError) {
          notifyFailure(MESSAGE.SYSTEM_ALERT_GET_UNREAD_FAILURE)
          return
        }

        this.alerts = response.unreadAlerts ?? []
      },
      async acknowledgeAlert({
        alertId,
        isAcknowledged,
      }: {
        alertId: string
        isAcknowledged: boolean | undefined
      }): Promise<boolean> {
        const acknowledgeAlertResponse =
          await HNRV2<TransferObjectsResponseDTO>({
            fn: () =>
              api.systemAlerts.apiSystemAlertsAcknowledgePost({
                systemAlertID: alertId,
                acknowledgePolicy: isAcknowledged,
              }),
          })

        if (acknowledgeAlertResponse instanceof AxiosError) {
          return false
        }

        return true
      },
      async getAlertDetailsForUser({ alertId }: { alertId: string }) {
        const getAlertResponse =
          await HNRV2<SystemAlertsSystemAlertDetailsForUser>({
            fn: () =>
              api.systemAlerts.apiSystemAlertsUserSystemAlertIDGet(alertId),
          })

        if (getAlertResponse instanceof AxiosError) {
          return
        }

        this.alertDetailsForUser = getAlertResponse
        return getAlertResponse
      },
    },
  }
)
